import React from 'react';
import { Link } from 'react-router-dom';
import {
  CalendarDays, FileHeart, History, Network, Pill, Stethoscope,
} from 'lucide-react';
import { IITH, SERIF } from '@/pages/landing/iithBrand';

const ALL_ACTIONS = [
  {
    to: '/patient/appointments',
    label: 'My appointments',
    hint: 'Track upcoming & past visits',
    icon: CalendarDays,
    testid: 'pt-action-appts',
  },
  {
    to: '/patient/appointments/book',
    label: 'Book OPD',
    hint: 'New slot with campus doctors',
    icon: Stethoscope,
    testid: 'pt-action-book',
  },
  {
    to: '/patient/referrals/new',
    label: 'Referrals',
    hint: 'Request & track letters',
    icon: FileHeart,
    testid: 'pt-action-refs',
    facultyOnly: true,
  },
  {
    to: '/patient/family',
    label: 'Family tree',
    hint: 'Dependents on your record',
    icon: Network,
    testid: 'pt-action-family',
    facultyOnly: true,
  },
  {
    to: '/patient/care',
    label: 'Care history',
    hint: 'Visits, diagnoses & notes',
    icon: History,
    testid: 'pt-action-care',
  },
  {
    to: '/patient/care#prescriptions',
    label: 'Prescriptions',
    hint: 'Medicines from past visits',
    icon: Pill,
    testid: 'pt-action-rx',
  },
];

/** Hub of primary patient self-service actions. */
export default function PatientQuickActions({ primary = IITH.saffron, isFacultyStaff = false }) {
  const actions = ALL_ACTIONS.filter((a) => isFacultyStaff || !a.facultyOnly);
  return (
    <section className="mb-10" data-testid="patient-quick-actions">
      <h2 className="text-xl font-bold text-[#071B34] mb-1" style={{ fontFamily: SERIF }}>
        What would you like to do?
      </h2>
      <p className="text-sm text-[#5A6B7C] mb-4">
        {isFacultyStaff
          ? 'Everything for you and your family at the campus hospital.'
          : 'Book visits and track your own medical records.'}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {actions.map(({ to, label, hint, icon: Icon, testid }) => (
          <Link
            key={to}
            to={to}
            data-testid={testid}
            className="group flex gap-3 rounded-2xl border border-[#D5DEE8] bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
          >
            <span
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-white"
              style={{ background: `linear-gradient(145deg, ${primary}, #9a3412)` }}
            >
              <Icon className="h-5 w-5" />
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-semibold text-[#071B34] group-hover:underline">{label}</span>
              <span className="block text-[12px] text-[#7A8B9C] mt-0.5">{hint}</span>
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
}
